<template>
  <v-dialog @input="close" :value="isOpened" max-width="700px">
    <v-card>
      <v-toolbar dark color="primary">
        <v-toolbar-title class="font-weight-bold">
          {{ action }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn @click="close" icon>
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="pt-4 px-3">
        <v-form ref="form">
          <v-container>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="updates.name"
                  :label="`${$t('generalAttributes.name')}*`"
                  :rules="[requiredStringRule]"
                  counter="256" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="updates.legal_name"
                  :label="`${$t('entities.companies.attributes.legalName')}*`"
                  :rules="[requiredStringRule]"
                  counter="256" />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="updates.tax_number"
                  :label="`${$t('entities.companies.attributes.taxNumber')}*`"
                  :rules="[requiredStringRule]"
                  counter="256"
                  :disabled="!isAdmin" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="updates.address1"
                  :label="`${$t('generalAttributes.address1')}*`"
                  :rules="[requiredStringRule]"
                  counter="256" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="updates.address2"
                  :label="$t('generalAttributes.address2')"
                  :rules="[optionalStringRule]"
                  counter="256" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="updates.city"
                  :label="`${$t('generalAttributes.city')}*`"
                  :rules="[requiredStringRule]"
                  counter="256" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="updates.postcode"
                  :label="`${$t('generalAttributes.postalCode')}*`"
                  :rules="[requiredStringRule]"
                  counter="256" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="updates.country"
                  :label="`${$t('entities.countries.entity')}*`"
                  :rules="[requiredStringRule]"
                  counter="256" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="updates.country_code"
                  :label="`${$t('generalAttributes.countryCode')}*`"
                  :rules="[requiredStringRule]"
                  counter="256" />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="updates.state"
                  :label="`${$t('generalAttributes.state')}*`"
                  :rules="[requiredStringRule]"
                  counter="256" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="updates.phone"
                  :label="$t('generalAttributes.phone')"
                  :rules="[optionalStringRule]"
                  counter="256" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="updates.email"
                  :label="$t('generalAttributes.email')"
                  :rules="[optionalStringRule]"
                  counter="256" />
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
      <v-divider />
      <v-card-actions class="py-4">
        <v-btn @click="close" text class="ml-2">
          {{ $t('miscellaneous.cancel') }}
        </v-btn>
        <v-spacer />
        <v-btn
          @click="submit"
          :loading="isLoading"
          color="primary"
          class="mr-2">
          {{ $t('miscellaneous.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import isRole from '@/mixins/crud/computed/isRole'

const resetUpdates = (data = {}) => ({
  id: data.id || '',
  name: data.name || '',
  legal_name: data.legal_name || '',
  tax_number: data.tax_number || '',
  address1: data.address1 || '',
  address2: data.address2 || '',
  postcode: data.postcode || '',
  city: data.city || '',
  state: data.state || '',
  country: data.country || '',
  country_code: data.country_code || '',
  phone: data.phone || '',
  email: data.email || '',
})

export default {
  name: 'CompanyUpsertDialog',
  props: {
    company: { type: Object, default: () => ({}) },
    isLoading: { type: Boolean, default: false },
    isOpened: { type: Boolean, required: true },
  },
  mixins: [isRole],
  data() {
    return {
      updates: resetUpdates(),
    }
  },
  computed: {
    action() {
      return this.company.id
        ? this.$t('entities.companies.editEntity')
        : this.$t('entities.companies.newEntity')
    },
    optionalStringRule() {
      return (v) => !v || v.length <= 256 || 'Must be 256 characters or less.'
    },
    requiredStringRule() {
      return (v) =>
        (!!v && v.length >= 1 && v.length <= 256) ||
        'Required (1-256 characters).'
    },
  },
  methods: {
    buildPayload() {
      const payload = { ...this.updates }
      ;['address2', 'phone', 'email'].forEach((field) => {
        if (payload[field] === '') {
          payload[field] = null
        }
      })
      return payload
    },
    close() {
      return this.$emit('dialog:close')
    },
    onClose() {
      this.updates = resetUpdates()
      this.resetForm()
    },
    onOpen() {
      this.resetForm()
      this.updates = resetUpdates(this.company)
    },
    resetForm() {
      if (this.$refs.form) this.$refs.form.reset()
    },
    save() {
      this.$emit('company:upsert', this.buildPayload())
    },
    submit() {
      if (!this.$refs.form || !this.$refs.form.validate()) return
      this.save()
    },
  },
  watch: {
    isOpened(value) {
      return value ? this.onOpen() : this.onClose()
    },
  },
}
</script>
